import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@goaltree/ui/components/sheet'
import { POST_KIND_COLORS, profileUrl } from '@goaltree/ui/lib/social-share'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { ConvexError } from 'convex/values'
import { Lock, PenLine, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CanopyAvatar } from '@/components/canopy-avatar'
import { useFollowToggle } from '@/components/follow-button'
import { GrowthSection } from '@/components/growth-section'
import { KIND_TEXT } from '@/components/new-post-dialog'
import { StreakFlame } from '@/components/streak-icons'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { useIsMobile } from '@/hooks/use-mobile'
import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'
import { canNativeShare, nativeShare } from '@/lib/share'
import { goalSlug } from '@/lib/utils'

const OWN_PREVIEW = 3

type Profile = FunctionReturnType<typeof api.leaderboard.getUserPublicProfile>
type Goal = Profile['goals'][number]

type Props = {
  userId: string | null
  name: string
  image: string | null
  onClose: () => void
}

function ago(ms: number, locale: string) {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' })
  const mins = Math.round((Date.now() - ms) / 60_000)
  if (mins < 60) return rtf.format(-Math.max(mins, 1), 'minute')
  const hours = Math.round(mins / 60)
  if (hours < 24) return rtf.format(-hours, 'hour')
  return rtf.format(-Math.round(hours / 24), 'day')
}

function level(n: number) {
  return n === 0 ? 'bg-muted' : n === 1 ? 'bg-primary/35' : n <= 3 ? 'bg-primary/65' : 'bg-primary'
}

function SectionLabel({ children, meta }: { children: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/40">
      {children}
      {meta && (
        <span className="text-[11px] font-medium normal-case tracking-normal text-muted-foreground">
          {meta}
        </span>
      )}
    </div>
  )
}

type SheetUser = { userId: string; name: string; image: string | null }

export function LeaderboardUserSheet({ userId, name, image, onClose: closeSheet }: Props) {
  const { t, i18n } = useTranslation()
  // Back from a goal opened here returns to the leaderboard, not to a reopened sheet.
  const onClose = closeSheet
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  // Freeze the last-selected user so the content (and avatar) stays stable while
  // the sheet animates closed — otherwise userId→null blanks the DiceBear seed
  // and flashes a different cartoon on the way out.
  const [shown, setShown] = useState<SheetUser | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [copyingId, setCopyingId] = useState<string | null>(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  useEffect(() => {
    if (userId) {
      setShown({ userId, name, image })
      setShowAll(false)
    }
  }, [userId, name, image])
  const profile = useQuery(
    api.leaderboard.getUserPublicProfile,
    shown ? { userId: shown.userId } : 'skip',
  )
  const copyPublicGoal = useMutation(api.nodes.copyPublicGoal)
  const follow = useFollowToggle(profile?.handle ?? null, shown?.userId ?? '', profile?.isFollowing)

  const isMe = !!profile?.isMe
  const handle = profile?.handle ?? null

  function openGoal(goal: Goal) {
    // The friend view redirects owners to their own goal view.
    navigate({ to: '/friends/goals/$goalId', params: { goalId: goalSlug(goal.title, goal.id) } })
    onClose()
  }

  async function usePlan(goal: Goal) {
    if (copyingId) return
    setCopyingId(goal.id)
    try {
      const newId = await copyPublicGoal({ goalId: goal.id as Id<'nodes'> })
      toast.success(t('leaderboard.profile.planCopied'), {
        action: {
          label: t('leaderboard.profile.view'),
          onClick: () => {
            navigate({ to: '/goals/$goalId', params: { goalId: goalSlug(goal.title, newId) } })
            onClose()
          },
        },
      })
    } catch (err) {
      if (err instanceof ConvexError && String(err.data).includes('Free plan is limited'))
        setUpgradeOpen(true)
      else toast.error(t('leaderboard.profile.copyFailed'))
    } finally {
      setCopyingId(null)
    }
  }

  async function shareProfile() {
    if (!handle) return
    const url = withShareUtm(profileUrl(handle), 'profile_link')
    if (isMobile && canNativeShare()) {
      await nativeShare({ text: profile?.name ?? '', url })
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      toast.success(t('leaderboard.profile.linkCopied'))
    } catch {}
  }

  function writePost() {
    onClose()
    window.dispatchEvent(new CustomEvent('open-new-post'))
  }

  const goals = profile?.goals ?? []
  const privateCount = goals.filter((g) => g.isPrivate).length
  const visibleGoals = isMe && !showAll ? goals.slice(0, OWN_PREVIEW) : goals
  const posts = profile?.recentPosts ?? []
  const activity = profile?.activity ?? []
  const joined = profile?.joinedAt
    ? new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(profile.joinedAt)
    : null
  const btn =
    'flex h-9 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full border border-border text-xs font-bold text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50'

  return (
    <Sheet open={!!userId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-80 flex-col gap-4 overflow-y-auto p-5 sm:max-w-sm"
      >
        <div className="flex items-center gap-3">
          <CanopyAvatar userId={shown?.userId}>
            <UserAvatar
              key={shown?.userId}
              image={shown?.image ?? profile?.gravatar}
              name={shown?.name}
              className="size-13 shrink-0 rounded-full object-cover"
            />
          </CanopyAvatar>
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate text-lg font-extrabold tracking-tight text-foreground">
              {profile?.name ?? shown?.name}
            </SheetTitle>
            {profile && (handle || joined) && (
              <p className="flex min-w-0 font-mono text-xs text-muted-foreground">
                {handle && <span className="min-w-0 truncate">@{handle}</span>}
                {joined && (
                  <span className="shrink-0 whitespace-nowrap">
                    {handle && ' · '}
                    {t('leaderboard.profile.joined', { date: joined })}
                  </span>
                )}
              </p>
            )}
          </div>
          <SheetClose
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center self-start rounded-full text-foreground/40 transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t('leaderboard.profile.close')}
          >
            <X className="h-4 w-4" />
          </SheetClose>
        </div>

        {profile === undefined ? (
          <>
            <div className="flex gap-2">
              <div className="h-9 flex-1 animate-pulse rounded-full bg-muted/40" />
              <div className="h-9 flex-1 animate-pulse rounded-full bg-muted/40" />
            </div>
            <div className="flex overflow-hidden rounded-xl border border-border">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex-1 border-r border-border px-2.5 py-2 last:border-r-0">
                  <div className="h-4 w-8 animate-pulse rounded bg-muted/50" />
                  <div className="mt-1.5 h-2 w-10 animate-pulse rounded bg-muted/40" />
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <div className="h-2.5 w-20 animate-pulse rounded bg-muted/40" />
              <div className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-1">
                {Array.from({ length: 14 }, (_, i) => (
                  <div key={i} className="aspect-square animate-pulse rounded-[4px] bg-muted/40" />
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <div className="h-2.5 w-14 animate-pulse rounded bg-muted/40" />
              {[70, 55, 62].map((w) => (
                <div key={w} className="space-y-2 border-b border-border/60 py-2.5 last:border-b-0">
                  <div
                    className="h-3.5 animate-pulse rounded bg-muted/50"
                    style={{ width: `${w}%` }}
                  />
                  <div className="ml-[15px] h-2.5 w-1/2 animate-pulse rounded bg-muted/30" />
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {isMe ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  className={btn}
                  onClick={() => {
                    navigate({ to: '/settings', hash: 'profile' })
                    onClose()
                  }}
                >
                  {t('leaderboard.profile.editProfile')}
                </button>
                {handle && (
                  <a href={profileUrl(handle)} target="_blank" rel="noopener" className={btn}>
                    {t('leaderboard.profile.viewPublic')} ↗
                  </a>
                )}
              </div>
            ) : (
              handle && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={!follow.known}
                    onClick={() => void follow.toggle()}
                    className={cn(
                      btn,
                      !follow.isFollowing &&
                        'border-primary bg-primary text-white hover:bg-primary/90 dark:text-[#0a0a0a]',
                    )}
                  >
                    {follow.isFollowing
                      ? t('leaderboard.following')
                      : `+ ${t('leaderboard.follow')}`}
                  </button>
                  <button type="button" className={btn} onClick={() => void shareProfile()}>
                    {t('leaderboard.profile.share')}
                  </button>
                </div>
              )
            )}

            <div className="flex overflow-hidden rounded-xl border border-border">
              {(
                [
                  [
                    <>
                      <StreakFlame /> {profile.streak}
                    </>,
                    t('leaderboard.profile.dayStreak'),
                  ],
                  [
                    profile.weekRank ? `#${profile.weekRank}` : '–',
                    t('leaderboard.profile.thisWeek'),
                  ],
                  [String(profile.weekCount ?? 0), t('leaderboard.profile.tasksThisWk')],
                  [
                    String(profile.goalCount ?? goals.length),
                    t('leaderboard.profile.goals', { count: profile.goalCount ?? goals.length }),
                  ],
                ] as [React.ReactNode, string][]
              ).map(([value, label]) => (
                <div
                  key={label}
                  className="min-w-0 flex-1 border-r border-border px-2.5 py-2 last:border-r-0"
                >
                  <p className="text-[15px] font-extrabold tabular-nums text-foreground">{value}</p>
                  <p className="line-clamp-2 text-[10px] leading-tight text-muted-foreground">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            {activity.length > 0 && (
              <div className="space-y-1.5">
                <SectionLabel
                  meta={
                    profile.bestStreak
                      ? t('leaderboard.profile.bestStreak', { count: profile.bestStreak })
                      : undefined
                  }
                >
                  {t('leaderboard.profile.last14')}
                </SectionLabel>
                <div className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-1">
                  {activity.map((n, i) => (
                    <span
                      key={i}
                      title={t('leaderboard.profile.dayCount', { count: n })}
                      className={cn(
                        'aspect-square rounded-[4px]',
                        level(n),
                        i === activity.length - 1 &&
                          'outline-[1.5px] outline-offset-1 outline-foreground outline-solid',
                      )}
                    />
                  ))}
                </div>
              </div>
            )}

            {shown && <GrowthSection userId={shown.userId} />}

            {(isMe || posts.length > 0) && (
              <div className="space-y-2">
                <SectionLabel>{t('leaderboard.profile.recentPosts')}</SectionLabel>
                {posts.length === 0 ? (
                  <div className="flex flex-col items-start gap-2 rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-xs">
                    <span className="text-muted-foreground">
                      {t('leaderboard.profile.noPostsYet')}
                    </span>
                    <button
                      type="button"
                      onClick={writePost}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-primary/45 px-2 py-0.5 text-[10.5px] font-bold text-primary-text"
                    >
                      <PenLine className="h-3 w-3" />
                      {t('leaderboard.profile.writeFirst')}
                    </button>
                  </div>
                ) : (
                  posts.map((p) => (
                    <div
                      key={p.id}
                      className="flex flex-col gap-1.5 rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-xs text-foreground"
                    >
                      <div className="flex min-w-0 items-center gap-2 text-[10.5px] text-muted-foreground">
                        {p.kind && (
                          <>
                            <span
                              className="h-1.5 w-1.5 shrink-0 rounded-full"
                              style={{ background: POST_KIND_COLORS[p.kind] }}
                            />
                            <span className={cn('font-bold', KIND_TEXT[p.kind])}>
                              {t(`postComposer.kind.${p.kind}`)}
                            </span>
                            <span>·</span>
                          </>
                        )}
                        <span className="shrink-0">{ago(p.createdAt, i18n.language)}</span>
                        {p.goal && <span className="truncate">· {p.goal.title}</span>}
                      </div>
                      <p className="whitespace-pre-wrap break-words">{p.content}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            <div className="space-y-1">
              <SectionLabel
                meta={
                  isMe && privateCount > 0
                    ? t('leaderboard.profile.privateCount', {
                        count: goals.length,
                        private: privateCount,
                      })
                    : undefined
                }
              >
                {t('leaderboard.goals')}
              </SectionLabel>
              {goals.length === 0 && profile.privateGoalCount === 0 ? (
                <p className="py-6 text-center text-sm text-foreground/40">
                  {t('leaderboard.noGoalsYet')}
                </p>
              ) : (
                <div>
                  {visibleGoals.map((goal) => (
                    <GoalRow
                      key={goal.id}
                      goal={goal}
                      canCopy={!isMe && !goal.isPrivate && !goal.completed}
                      copying={copyingId === goal.id}
                      onOpen={() => openGoal(goal)}
                      onUsePlan={() => void usePlan(goal)}
                    />
                  ))}
                  {!isMe && profile.privateGoalCount > 0 && (
                    <p className="flex items-center gap-2 py-2.5 text-xs text-muted-foreground">
                      <Lock className="h-3.5 w-3.5" />
                      {t('leaderboard.profile.privateMore', { count: profile.privateGoalCount })}
                    </p>
                  )}
                  {isMe && goals.length > OWN_PREVIEW && (
                    <button
                      type="button"
                      onClick={() => setShowAll((s) => !s)}
                      className="cursor-pointer py-2.5 text-xs font-semibold text-primary-text"
                    >
                      {showAll
                        ? t('leaderboard.profile.showLess')
                        : `${t('leaderboard.profile.showAll', { count: goals.length })} →`}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="goal-limit" />
      </SheetContent>
    </Sheet>
  )
}

function GoalRow({
  goal,
  canCopy,
  copying,
  onOpen,
  onUsePlan,
}: {
  goal: Goal
  canCopy: boolean
  copying: boolean
  onOpen: () => void
  onUsePlan: () => void
}) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const color = fill(CATEGORY_COLORS[goal.category.toLowerCase()] ?? CATEGORY_COLORS.mind)
  const pct = Math.round(goal.progress)
  const ms = goal.currentMilestone
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      className="-mx-2 flex cursor-pointer flex-col gap-1 rounded-lg border-b border-border/60 px-2 py-2.5 transition-colors last:border-b-0 hover:bg-muted/30"
    >
      <div className="flex min-w-0 items-center gap-2 text-[13px] font-semibold">
        <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: color }} />
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            goal.completed ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {goal.title}
        </span>
        {goal.isPrivate && (
          <Lock
            className="h-3 w-3 shrink-0 text-muted-foreground"
            aria-label={t('leaderboard.profile.private')}
          />
        )}
        <span className="shrink-0 font-mono text-[10.5px] font-medium text-muted-foreground">
          {pct}%
        </span>
        {goal.completed && <span className="text-xs text-[#3f8f1f] dark:text-[#7ed957]">✓</span>}
      </div>
      {!goal.completed && (
        <div className="flex items-center gap-2 pl-[15px] text-[11px] text-muted-foreground">
          {ms && (
            <span className="min-w-0 truncate">
              {t('leaderboard.profile.milestone', {
                index: ms.index,
                total: ms.total,
                title: ms.title,
              })}
            </span>
          )}
          <span className="ml-auto h-[3px] w-full max-w-[110px] shrink-0 basis-[70px] overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full rounded-full"
              style={{ width: `${pct}%`, background: color }}
            />
          </span>
          {canCopy && (
            <button
              type="button"
              disabled={copying}
              onClick={(e) => {
                e.stopPropagation()
                onUsePlan()
              }}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-full border border-primary/45 px-2 py-px text-[10.5px] font-bold text-primary-text disabled:opacity-50"
            >
              {t('leaderboard.profile.usePlan')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
